(() => {
  if (window.__eraspaceAnimationPerformanceLoaded) return;
  window.__eraspaceAnimationPerformanceLoaded = true;

  const MOBILE_QUERY = window.matchMedia('(max-width: 699px)');
  const DESKTOP_TARGET_FPS = 60;
  const MOBILE_TARGET_FPS = 30;
  const MONITOR_WINDOW_MS = 6500;
  const STABLE_FRAME_COUNT = 36;
  const MISSED_FRAME_LIMIT = 2;
  const active = new WeakMap();

  const root = document.documentElement;
  const getTargetFps = () => MOBILE_QUERY.matches ? MOBILE_TARGET_FPS : DESKTOP_TARGET_FPS;
  const getFrameBudget = () => 1000 / getTargetFps();

  const setTarget = () => {
    const target = String(getTargetFps());
    if (root.dataset.eraspaceFpsTarget !== target) root.dataset.eraspaceFpsTarget = target;
  };

  const parseTimeList = (value) => value.split(',').map((part) => {
    const trimmed = part.trim();
    if (!trimmed) return 0;
    return trimmed.endsWith('ms') ? Number.parseFloat(trimmed) : Number.parseFloat(trimmed) * 1000;
  });

  const hasMotion = (styles) => {
    const transitionDuration = Math.max(...parseTimeList(styles.transitionDuration), 0);
    const animationDuration = Math.max(...parseTimeList(styles.animationDuration), 0);
    return transitionDuration > 0 || (styles.animationName !== 'none' && animationDuration > 0);
  };

  const promote = (el) => {
    if (!(el instanceof HTMLElement) || active.has(el)) return;

    active.set(el, {
      willChange: el.style.willChange,
      backfaceVisibility: el.style.backfaceVisibility,
    });

    el.classList.add('eraspace-animation-active');
    if (!el.style.willChange) el.style.willChange = 'transform, opacity';
    if (!el.style.backfaceVisibility) el.style.backfaceVisibility = 'hidden';
  };

  const release = (el) => {
    const previous = active.get(el);
    if (!previous) return;

    el.classList.remove('eraspace-animation-active');
    el.style.willChange = previous.willChange;
    el.style.backfaceVisibility = previous.backfaceVisibility;
    active.delete(el);
  };

  const releaseSoon = (el) => {
    window.setTimeout(() => release(el), MOBILE_QUERY.matches ? 420 : 260);
  };

  const promoteIfAnimated = (target) => {
    if (!(target instanceof HTMLElement)) return;
    if (hasMotion(window.getComputedStyle(target))) promote(target);
  };

  document.addEventListener('transitionrun', (event) => promoteIfAnimated(event.target), true);
  document.addEventListener('transitionstart', (event) => promoteIfAnimated(event.target), true);
  document.addEventListener('animationstart', (event) => promoteIfAnimated(event.target), true);
  document.addEventListener('transitionend', (event) => releaseSoon(event.target), true);
  document.addEventListener('transitioncancel', (event) => releaseSoon(event.target), true);
  document.addEventListener('animationend', (event) => releaseSoon(event.target), true);
  document.addEventListener('animationcancel', (event) => releaseSoon(event.target), true);

  let monitorFrame = 0;
  let monitorStarted = 0;
  let lastFrame = 0;
  let missedFrames = 0;
  let stableFrames = 0;

  const setConserve = (enabled) => {
    root.classList.toggle('eraspace-animation-conserve', enabled);
  };

  const stopMonitor = () => {
    if (monitorFrame) window.cancelAnimationFrame(monitorFrame);
    monitorFrame = 0;
    lastFrame = 0;
  };

  const monitorFrames = (time) => {
    if (!monitorStarted) monitorStarted = time;

    if (lastFrame && !document.hidden) {
      const frameDelta = time - lastFrame;
      const budget = getFrameBudget();

      if (frameDelta > budget * 1.45) {
        missedFrames += 1;
        stableFrames = 0;
      } else {
        stableFrames += 1;
        if (stableFrames >= STABLE_FRAME_COUNT) {
          missedFrames = 0;
          setConserve(false);
        }
      }

      if (missedFrames >= MISSED_FRAME_LIMIT) setConserve(true);
    }

    lastFrame = time;

    const stillSettling = time - monitorStarted < MONITOR_WINDOW_MS;
    const stillRecovering = root.classList.contains('eraspace-animation-conserve') && stableFrames < STABLE_FRAME_COUNT;

    if (stillSettling || stillRecovering) {
      monitorFrame = window.requestAnimationFrame(monitorFrames);
      return;
    }

    stopMonitor();
  };

  const startMonitor = () => {
    setTarget();
    if (monitorFrame) return;

    monitorStarted = 0;
    lastFrame = 0;
    missedFrames = 0;
    stableFrames = 0;
    monitorFrame = window.requestAnimationFrame(monitorFrames);
  };

  const boot = () => {
    root.dataset.eraspacePerfLayer = 'lean';
    startMonitor();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', () => startMonitor());
  window.addEventListener('pageshow', startMonitor, { passive: true });
  window.addEventListener('resize', startMonitor, { passive: true });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) startMonitor();
  });

  MOBILE_QUERY.addEventListener?.('change', () => {
    setTarget();
    startMonitor();
  });
})();
